import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTransition } from '../context/TransitionContext';
import '../styles/components/CurtainTransition.css';

const CurtainTransition = ({ children }) => {
  const location = useLocation();
  const { canRender } = useTransition();

  // Layer 2 - Middle
  const layer2Variants = {
    initial: {
      scaleX: 1,
      transformOrigin: 'left',
    },
    animate: {
      scaleX: 0,
      transformOrigin: 'left',
      transition: {
        duration: 0.9,
        delay: 0.5,
        ease: [0.76, 0, 0.24, 1],
      },
    },
    exit: {
      scaleX: 1,
      transformOrigin: 'left',
      transition: {
        duration: 0.8,
        delay: 0.1,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  // Layer 3 - Top (Last to open, with logo)
  const layer3Variants = {
    initial: {
      scaleX: 1,
      transformOrigin: 'left',
    },
    animate: {
      scaleX: 0,
      transformOrigin: 'left',
      transition: {
        duration: 1,
        delay: 0.6,
        ease: [0.76, 0, 0.24, 1],
      },
    },
    exit: {
      scaleX: 1,
      transformOrigin: 'left',
      transition: {
        duration: 0.9,
        delay: 0.2,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  // Logo - Smooth fade and scale
  const logoVariants = {
    initial: {
      opacity: 0,
      scale: 0.7,
      rotate: -10,
    },
    animate: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        delay: 0.3,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      scale: 0.7,
      rotate: 10,
      transition: {
        duration: 0.4,
        ease: 'easeIn',
      },
    },
  };

  return (
    <AnimatePresence mode="wait">
      <div key={location.pathname} className="curtain-transition-wrapper">
        {/* Layer 2 - Nova Blue */}
        <motion.div
          className="curtain-layer curtain-layer-2"
          variants={layer2Variants}
          initial="initial"
          animate="animate"
          exit="exit"
        />

        {/* Layer 3 - Space Blue (with Logo) */}
        <motion.div
          className="curtain-layer curtain-layer-3"
          variants={layer3Variants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <motion.div
            className="curtain-logo"
            variants={logoVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" viewBox="0 0 65 75" fill="none">
              <path d="M5.02684 38.8821C7.45261 31.3409 26.8461 29.6064 44.2538 34.7847C45.1713 35.0612 46.0637 35.3503 46.931 35.6519C43.5123 33.9426 39.6662 32.3966 35.5186 31.1523C18.4 26.0117 2.61364 27.7336 0.288429 34.9984C-0.930737 38.8067 1.78411 43.3943 7.12582 47.6299C4.97657 44.5632 4.19731 41.4964 5.02684 38.8821Z" fill="white" />
              <path d="M57.074 43.4826C57.074 43.4826 57.0363 43.4826 57.0237 43.4701C59.9899 46.5117 61.3599 49.5408 60.5304 52.0922C60.1408 53.2737 59.2987 54.2415 58.0795 55.0207C58.0166 55.0584 57.9664 55.0961 57.9035 55.1339C57.7778 55.2093 57.6647 55.2847 57.5265 55.3475C56.7472 55.7749 55.8297 56.1519 54.8116 56.4536C54.3717 56.5918 53.9318 56.7426 53.4919 56.8809C45.2217 59.1056 34.4503 57.7104 25.2374 55.1967C24.5713 55.0584 23.9177 54.9076 23.2516 54.7442C21.1023 54.2415 19.041 53.6507 17.08 52.9972C17.1055 53.0349 17.1306 53.0726 17.1683 53.0852C19.3553 54.3169 21.6428 55.4229 23.9931 56.4033C31.107 59.3695 38.6859 61.3931 46.4911 61.7701C50.5131 61.921 54.8745 61.9712 58.7456 60.3122C67.9962 56.1142 64.9797 47.0396 57.0866 43.4701L57.074 43.4826Z" fill="white" />
              <path d="M24.6843 68.758C15.0064 65.1759 10.6073 53.0094 12.5555 38.1658C8.16899 55.8374 11.9773 71.5986 21.8438 74.5271C28.2412 76.425 35.7448 72.4909 42.1171 64.8743C36.1093 69.022 29.7872 70.6559 24.6843 68.758Z" fill="white" />
              <path d="M44.8444 0.484767C35.1665 -2.45631 22.9748 8.26481 16.0117 25.0566C23.1256 13.0158 32.9921 6.06528 41.1492 8.55389C52.0462 11.872 55.7289 30.788 49.3691 50.7974C49.168 51.4384 48.9543 52.0668 48.7407 52.6952C50.2866 49.5908 51.6692 46.2349 52.8004 42.6528C59.1476 22.6309 55.5906 3.75263 44.857 0.484767H44.8444Z" fill="white" />
            </svg>
          </motion.div>
        </motion.div>

        {/* Page Content - Only render when transition complete */}
        <div 
          className="curtain-content"
          style={{ 
            opacity: canRender ? 1 : 0,
            transition: 'opacity 0.5s ease'
          }}
        >
          {canRender && children}
        </div>
      </div>
    </AnimatePresence>
  );
};

export default CurtainTransition;